import React, { createContext, useMemo, useContext } from 'react';
import translator from '@prisjakt/ui-translate/src/translator';
import { ClientMarket } from '@prisjakt/web-app-config/types/client-market';

import linkTo from './link-to';

export { default as linkTo } from './link-to';

export type AppContextValue = {
  renderMode: RENDER_MODES;
  market: ClientMarket;
  // linkto is "curried" (using `.bind`), when the context value is set - and as such
  // we cannot just do `typeof linkto` here.
  linkTo: (type: string, item?: any, query?: { [key: string]: any }) => string;
  t: ReturnType<typeof translator>;
  Translate: ReturnType<typeof translator>;
  captureMessage: (data: any) => void;
  captureException: (data: any) => void;
};
const crash = () => {
  throw new Error('AppContext value is not initialized.');
};
const initialValue: any = {
  renderMode: '',
  market: '',
  linkTo: crash,
  t: crash,
  Translate: crash,
  captureMessage: crash,
  captureException: crash,
};
export const AppContext = createContext<AppContextValue>(initialValue);
AppContext.displayName = 'AppContext';

const captureEvent = (
  sentryPromise: Promise<any>,
  sentryMethodName: string,
  data: any
) => {
  sentryPromise
    .then(sentry => {
      sentry[sentryMethodName](data);
    })
    .catch(() => {
      const consoleMethodName = {
        captureMessage: 'log',
        captureException: 'error',
      }[sentryMethodName];
      console[consoleMethodName](data); // eslint-disable-line no-console
    });
};

type Translations = {
  [domain: string]: {
    [text: string]: string;
  };
};

export const AppContextContainer = ({
  children,
  translations,
  renderMode,
  market,
  locale,
  sentryPromise,
}: {
  children: React.ReactNode;
  translations: Translations;
  market: NonNullable<AppContextValue>['market'];
  locale: string;
  sentryPromise: Promise<any>;
  renderMode: NonNullable<AppContextValue>['renderMode'];
}) => {
  const context = useMemo(() => {
    const translate = translator(translations, locale);
    return {
      renderMode,
      market,
      linkTo: linkTo.bind(
        undefined,
        market.useClassicUrls,
        market.useHashForProductTabs,
        market.classicRoutes,
        market.routes
      ),
      t: translate,
      Translate: translate,
      captureMessage: (data: string) =>
        captureEvent(sentryPromise, 'captureMessage', data),
      captureException: (data: Error) =>
        captureEvent(sentryPromise, 'captureException', data),
    };
  }, [translations, locale, renderMode, market, sentryPromise]);

  return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
};

// Needed to return an AppContext value for enzyme tests.
// It's a workaround for shallow rendering with React.useContext hook.
export const useAppContext = () => useContext(AppContext);

type RENDER_MODES = 'desktop' | 'mobile' | 'native' | 'responsive' | 'beta';

AppContextContainer.defaultProps = {
  children: null,
  locale: undefined,
};
